
import React from 'react';
import { type EnhancedCrawlResult } from '@/types/crawler';
import { useFeatureModel } from '@/contexts/FeatureModelContext';
import { logger } from '@/utils/logger';
import { toast } from 'sonner';
import DeepAnalysisWidget from './widgets/DeepAnalysisWidget';
import RecommendedWidget from './widgets/RecommendedWidget';
import FaqExpertWidget from './widgets/FaqExpertWidget';

interface DashboardWidgetsProps {
  crawlResult: EnhancedCrawlResult;
  selectedPageUrl: string | null;
  widgetRefs: {
    [key: string]: React.RefObject<HTMLDivElement>;
  };
  onSelectPage: (url: string) => void;
  retryHandlers: {
    sitemap: () => void;
    deepAnalysis: () => void;
    recommended: () => void;
    faqSerp: () => void;
  };
}

const DashboardWidgets: React.FC<DashboardWidgetsProps> = ({
  crawlResult,
  selectedPageUrl,
  widgetRefs,
  onSelectPage,
  retryHandlers
}) => {
  const { isFeatureEnabled, getModelForFeature } = useFeatureModel();
  
  const createRetryHandler = (featureId: 'sitemap' | 'deepAnalysis' | 'recommended' | 'faqSerp', handler: () => void) => {
    return () => {
      if (featureId === 'recommended') {
        toast.info(`Retrying ${featureId} with ${getModelForFeature(featureId)} model...`);
        
        logger.info(`Manual retry initiated for ${featureId}`, {
          component: `${featureId}Widget`,
          context: {
            featureId,
            model: getModelForFeature(featureId),
            timestamp: new Date()
          }
        });
        
        handler();
        return;
      }
      
      if (!isFeatureEnabled(featureId)) {
        toast.error(`The ${featureId} feature is currently disabled in admin settings`);
        return;
      }
      
      toast.info(`Retrying ${featureId} with ${getModelForFeature(featureId)} model...`);
      
      logger.info(`Manual retry initiated for ${featureId}`, {
        component: `${featureId}Widget`,
        context: {
          featureId,
          model: getModelForFeature(featureId),
          timestamp: new Date()
        }
      });
      
      handler();
    };
  };

  return (
    <>      
      <DeepAnalysisWidget 
        crawlResult={crawlResult}
        selectedPageUrl={selectedPageUrl}
        widgetRef={widgetRefs["deep-analysis"]}
        onSelectPage={onSelectPage}
        onRetry={createRetryHandler('deepAnalysis', retryHandlers.deepAnalysis)}
      />
      
      <RecommendedWidget 
        crawlResult={crawlResult}
        widgetRef={widgetRefs.recommended}
        onRetry={createRetryHandler('recommended', retryHandlers.recommended)}
      />
      
      <FaqExpertWidget 
        crawlResult={crawlResult}
        widgetRef={widgetRefs["faq-serp"]}
        onRetry={createRetryHandler('faqSerp', retryHandlers.faqSerp)}
      />
    </>
  );
}

export default DashboardWidgets;
